import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, Send, Copy, CheckCircle, XCircle, Loader2, Inbox, Link2 } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A' };
const STATUS = {
  invited: { label: 'Invite open', color: C.amber },
  accepted: { label: 'Activated', color: C.green },
  pending: { label: 'Awaiting review', color: C.amber },
  approved: { label: 'Approved — invite sent', color: C.green },
  rejected: { label: 'Rejected', color: C.red },
};

const Overline = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{children}</p>
);

const Chip = ({ status }) => {
  const cfg = STATUS[status] || { label: status, color: C.slate };
  return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${cfg.color}14`, color: cfg.color }}>{cfg.label}</span>;
};

export default function PanelGrowthPage() {
  const api = useApi();
  const [invites, setInvites] = useState(null);
  const [applications, setApplications] = useState(null);
  const [brokerages, setBrokerages] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', brokerage_name: '' });
  const [busy, setBusy] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(() => {
    api.get('/signup/invites').then(r => setInvites(r.data.invites)).catch(() => setInvites([]));
    api.get('/signup/applications').then(r => setApplications(r.data.applications)).catch(() => setApplications([]));
    api.get('/signup/brokerages').then(r => setBrokerages(r.data.brokerages || [])).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const sendInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/signup/invites', form);
      toast.success(`Invite emailed to ${data.broker_email} — link valid 7 days`);
      setForm({ name: '', email: '', brokerage_name: '' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not send invite'); }
    setBusy(false);
  };

  const copyLink = (inv) => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${inv.token}`);
    toast.success('Invite link copied');
  };

  const resend = async (inv) => {
    setActing(inv.invite_id);
    try { await api.post(`/signup/invites/${inv.invite_id}/resend`); toast.success(`Invite re-sent to ${inv.broker_email}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Resend failed'); }
    setActing(null);
  };

  const decide = async (app, action) => {
    let reason = '';
    if (action === 'reject') {
      reason = window.prompt('Reason for rejecting this application (recorded in the audit log):', '');
      if (reason === null) return;
    }
    setActing(app.application_id);
    try {
      await api.post(`/signup/applications/${app.application_id}/${action}`,
        action === 'reject' ? { reason: reason || 'Not a fit for the panel at this time' } : {});
      toast.success(action === 'approve' ? `${app.name} approved — activation link emailed` : 'Application rejected');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Action failed'); }
    setActing(null);
  };

  if (!invites || !applications) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: C.blue }} /></div>;

  const pending = applications.filter(a => a.status === 'pending');
  const activated = invites.filter(i => i.status === 'accepted').length;

  return (
    <div className="space-y-5" data-testid="panel-growth-page">
      <div>
        <h2 className="text-xl font-medium tracking-tight" style={{ color: C.navy }}>Panel Growth</h2>
        <p className="text-sm mt-0.5" style={{ color: C.slate }}>Brokers sign themselves up — invite links activate instantly, open applications wait for your approval.</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[['Awaiting review', pending.length, pending.length ? C.amber : C.navy],
          ['Open invites', invites.filter(i => i.status === 'invited').length, C.blue],
          ['Self-activated brokers', activated, C.green]].map(([l, v, color]) => (
          <Card key={l} className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
            <Overline>{l}</Overline>
            <p className="text-3xl font-light tracking-tight mt-1" style={{ color, fontVariantNumeric: 'tabular-nums' }}>{v}</p>
          </Card>
        ))}
      </div>

      {/* Invite form */}
      <Card className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>
          <UserPlus className="w-3.5 h-3.5" style={{ color: C.blue }} />Invite a broker
        </h3>
        <form onSubmit={sendInvite} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>Broker name</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-sm text-sm" required data-testid="invite-name-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>Email</Label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1 rounded-sm text-sm" required data-testid="invite-email-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>Brokerage (company first)</Label>
            <Input list="brokerage-names" value={form.brokerage_name} onChange={e => setForm(f => ({ ...f, brokerage_name: e.target.value }))}
              placeholder="Pick existing or type new" className="mt-1 rounded-sm text-sm" data-testid="invite-brokerage-input" />
            <datalist id="brokerage-names">
              {brokerages.map(b => <option key={b.name} value={b.name} />)}
            </datalist>
          </div>
          <Button type="submit" disabled={busy} className="rounded-sm font-semibold text-white" style={{ background: C.blue, height: 40 }} data-testid="invite-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}Send invite
          </Button>
        </form>
        <p className="text-[10px] mt-2" style={{ color: C.slate }}>
          The broker gets a secure link, sets their own password and is aligned under the brokerage — no further admin needed.
        </p>
      </Card>

      {/* Applications queue */}
      <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="applications-queue">
        <div className="px-4 pt-3.5 pb-2 flex items-center gap-1.5">
          <Inbox className="w-3.5 h-3.5" style={{ color: C.blue }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Applications ({pending.length} awaiting review)</h3>
        </div>
        {applications.length === 0 ? (
          <p className="px-4 pb-4 text-xs" style={{ color: C.slate }}>No applications yet — brokers can apply from the sign-in page.</p>
        ) : (
          <div>
            {applications.slice(0, 20).map(a => (
              <div key={a.application_id} className="px-4 py-2.5 border-t flex items-center gap-3 flex-wrap" style={{ borderColor: 'rgba(136,150,168,0.1)' }}
                data-testid={`application-row-${a.email}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium" style={{ color: C.navy }}>{a.name} <span style={{ color: C.slate }}>· {a.email}</span></p>
                  <p className="text-[10px]" style={{ color: C.slate }}>{a.brokerage}{a.phone && ` · ${a.phone}`}{a.message && ` — “${a.message}”`}</p>
                </div>
                <Chip status={a.status} />
                {a.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button size="sm" disabled={acting === a.application_id} onClick={() => decide(a, 'approve')}
                      className="text-[11px] rounded-sm text-white h-7" style={{ background: C.green }} data-testid={`approve-application-${a.email}`}>
                      <CheckCircle className="w-3 h-3 mr-1" />Approve & invite
                    </Button>
                    <Button size="sm" variant="outline" disabled={acting === a.application_id} onClick={() => decide(a, 'reject')}
                      className="text-[11px] rounded-sm h-7" style={{ color: C.red, borderColor: 'rgba(226,75,74,0.3)' }} data-testid={`reject-application-${a.email}`}>
                      <XCircle className="w-3 h-3 mr-1" />Reject
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Invites table */}
      <Card className="rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="invites-table">
        <div className="px-4 pt-3.5 pb-2 flex items-center gap-1.5">
          <Link2 className="w-3.5 h-3.5" style={{ color: C.blue }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Invites ({invites.length})</h3>
        </div>
        {invites.length === 0 ? (
          <p className="px-4 pb-4 text-xs" style={{ color: C.slate }}>No invites sent yet.</p>
        ) : (
          <div>
            {invites.slice(0, 25).map(inv => (
              <div key={inv.invite_id} className="px-4 py-2.5 border-t flex items-center gap-3 flex-wrap" style={{ borderColor: 'rgba(136,150,168,0.1)' }}
                data-testid={`invite-row-${inv.broker_email}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium" style={{ color: C.navy }}>{inv.broker_name} <span style={{ color: C.slate }}>· {inv.broker_email}</span></p>
                  <p className="text-[10px]" style={{ color: C.slate }}>
                    {inv.brokerage_name || 'Independent'} · sent {String(inv.invited_at || '').slice(0, 10)}
                    {inv.source === 'application' && ' · from application'}
                    {inv.status === 'accepted' && inv.accepted_at && ` · activated ${String(inv.accepted_at).slice(0, 10)}`}
                  </p>
                </div>
                <Chip status={inv.status} />
                {inv.status === 'invited' && (
                  <div className="flex gap-1.5">
                    <button onClick={() => copyLink(inv)} title="Copy invite link" data-testid={`copy-invite-${inv.broker_email}`}
                      className="p-1.5 rounded hover:bg-blue-50 transition-colors"><Copy className="w-3.5 h-3.5" style={{ color: C.blue }} /></button>
                    <button onClick={() => resend(inv)} disabled={acting === inv.invite_id} title="Resend invite email" data-testid={`resend-invite-${inv.broker_email}`}
                      className="p-1.5 rounded hover:bg-blue-50 transition-colors disabled:opacity-50"><Send className="w-3.5 h-3.5" style={{ color: C.blue }} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
